import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  Shield,
  User,
  Check,
  X,
  Save,
  AlertTriangle,
  Crown,
  Key,
  Calendar,
  Clock,
  DollarSign,
  Users,
  MapPin,
  FileText,
  Sparkles,
  Settings,
  type LucideIcon,
} from 'lucide-react';
import { usePermissions } from '@/hooks/usePermissions';
import {
  Permission,
  Role,
  PERMISSION_GROUPS,
  PERMISSION_DESCRIPTIONS,
  ROLE_PERMISSIONS,
  isSensitivePermission,
} from '@/types/permissions';
import { StaffUser } from '@/types';
import { clsx } from 'clsx';

interface PermissionManagerProps {
  user: StaffUser;
  onSave?: (userId: number, newPermissions: Permission[], newRole: Role) => void;
  readonly?: boolean;
}

export const PermissionManager: React.FC<PermissionManagerProps> = ({
  user,
  onSave,
  readonly = false
}) => {
  const { hasPermission } = usePermissions();
  const { t } = useTranslation();

  const [selectedRole, setSelectedRole] = useState<Role>(user.role);
  const [customPermissions, setCustomPermissions] = useState<Permission[]>(user.permissions || []);
  const [hasChanges, setHasChanges] = useState(false);

  const canEditPermissions = hasPermission(Permission.ASSIGN_PERMISSIONS);

  const groupIconMap: Record<string, LucideIcon> = {
    calendar: Calendar,
    timeline: Clock,
    'dollar-sign': DollarSign,
    users: Users,
    'map-pin': MapPin,
    shield: Shield,
    'file-text': FileText,
    sparkles: Sparkles,
    settings: Settings,
  };

  const roleDefaultPermissions = ROLE_PERMISSIONS[selectedRole] || [];

  const effectivePermissions = [
    ...new Set([...roleDefaultPermissions, ...customPermissions])
  ];

  const handleRoleChange = (newRole: Role) => {
    if (readonly || !canEditPermissions) return;
    setSelectedRole(newRole);
    setHasChanges(true);
  };

  const handlePermissionToggle = (permission: Permission) => {
    if (readonly || !canEditPermissions) return;

    const isInRole = roleDefaultPermissions.includes(permission);
    const isInCustom = customPermissions.includes(permission);

    if (isInRole) {
      if (!isInCustom) {
        setCustomPermissions(prev => [...prev, permission]);
      } else {
        setCustomPermissions(prev => prev.filter(p => p !== permission));
      }
    } else {
      if (isInCustom) {
        setCustomPermissions(prev => prev.filter(p => p !== permission));
      } else {
        setCustomPermissions(prev => [...prev, permission]);
      }
    }

    setHasChanges(true);
  };

  const handleSave = () => {
    if (!canEditPermissions || !onSave) return;

    const finalCustomPermissions = customPermissions.filter(
      p => !roleDefaultPermissions.includes(p)
    );

    onSave(user.id, finalCustomPermissions, selectedRole);
    setHasChanges(false);
  };

  const getPermissionStatus = (permission: Permission) => {
    const isInRole = roleDefaultPermissions.includes(permission);
    const isInCustom = customPermissions.includes(permission);

    if (isInRole && !isInCustom) return 'role';
    if (isInRole && isInCustom) return 'removed';
    if (!isInRole && isInCustom) return 'added';
    return 'none';
  };

  const roleNameMap: Record<Role, string> = {
    [Role.ADMIN]: t('debug.roles.admin'),
    [Role.MANAGER]: t('debug.roles.manager'),
    [Role.SUPERVISOR]: t('debug.roles.supervisor'),
    [Role.STAFF]: t('debug.roles.staff'),
    [Role.TRAINEE]: t('debug.roles.trainee'),
  };

  const roleDescriptionMap: Record<Role, string> = {
    [Role.ADMIN]: t('permissionManager.adminRoleDesc'),
    [Role.MANAGER]: t('permissionManager.managerRoleDesc'),
    [Role.SUPERVISOR]: t('permissionManager.supervisorRoleDesc'),
    [Role.STAFF]: t('permissionManager.staffRoleDesc'),
    [Role.TRAINEE]: t('permissionManager.traineeRoleDesc'),
  };

  const statusLabelMap: Record<'role' | 'added' | 'removed', string> = {
    role: t('permissionManager.rolePermission'),
    added: t('permissionManager.addedPermission'),
    removed: t('permissionManager.removedPermission'),
  };

  return (
    <div className="space-y-6">
      <div className="card card-comfortable">
        <div className="flex items-center gap-4 mb-4">
          <div className="w-16 h-16 rounded-xl bg-gradient-primary flex items-center justify-center">
            <User className="h-8 w-8 text-white" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-gray-900">{user.full_name}</h3>
            <p className="text-sm text-gray-600">{user.email}</p>
            <p className="text-xs text-gray-500">
              {t('settings.userId')}: {user.id} • {t('settings.lastLogin')}: {user.last_login_at || t('permissionManager.neverLoggedIn')}
            </p>
          </div>
          {!readonly && canEditPermissions && hasChanges && (
            <button
              onClick={handleSave}
              className="ml-auto btn-primary flex items-center gap-2"
            >
              <Save className="h-4 w-4" />
              {t('common.save')}
            </button>
          )}
        </div>

        {!canEditPermissions && (
          <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-yellow-600" />
            <span className="text-sm text-yellow-700">
              {t('permissionManager.noPermissionToEdit')}
            </span>
          </div>
        )}
      </div>

      <div className="card card-comfortable">
        <div className="flex items-center gap-2 mb-4">
          <Crown className="h-5 w-5 text-amber-600" />
          <h4 className="text-lg font-semibold text-gray-900">
            {t('permissionManager.roleSetting')}
          </h4>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {Object.values(Role).map((role) => {
            const isSelected = selectedRole === role;
            const permissionCount = ROLE_PERMISSIONS[role]?.length || 0;

            return (
              <motion.button
                key={role}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => handleRoleChange(role)}
                disabled={readonly || !canEditPermissions}
                className={clsx(
                  "p-4 rounded-xl border-2 text-left transition-all",
                  isSelected
                    ? "border-primary-300 bg-primary-50 text-primary-900"
                    : "border-gray-200 bg-white text-gray-700 hover:border-gray-300",
                  (readonly || !canEditPermissions) && "opacity-50 cursor-not-allowed"
                )}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium">{roleNameMap[role]}</span>
                  {isSelected && <Check className="h-4 w-4 text-primary-600" />}
                </div>
                <p className="text-xs text-gray-600 mb-2">
                  {roleDescriptionMap[role]}
                </p>
                <p className="text-xs text-gray-500">
                  {permissionCount} {t('staff.permissions')}
                </p>
              </motion.button>
            );
          })}
        </div>
      </div>

      <div className="card card-comfortable">
        <div className="flex items-center gap-2 mb-6">
          <Key className="h-5 w-5 text-blue-600" />
          <h4 className="text-lg font-semibold text-gray-900">
            {t('permissionManager.detailedPermissions')}
          </h4>
          <span className="text-sm text-gray-500">
            ({effectivePermissions.length} {t('staff.permissions')})
          </span>
        </div>

        <div className="space-y-6">
          {Object.entries(PERMISSION_GROUPS).map(([groupKey, group]) => {
            const groupPermissions = group.permissions;
            if (groupPermissions.length === 0) {
              return null;
            }

            return (
              <div key={groupKey} className="space-y-3">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center">
                    {(() => {
                      const IconComponent = groupIconMap[group.icon];
                      return IconComponent
                        ? <IconComponent className="h-4 w-4 text-gray-600" />
                        : <span className="text-sm">{group.icon}</span>;
                    })()}
                  </div>
                  <h5 className="font-medium text-gray-900">
                    {t(`permissionGroups.${groupKey}`, { defaultValue: group.name })}
                  </h5>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 ml-10">
                  {groupPermissions.map((permission) => {
                    const status = getPermissionStatus(permission);
                    const isSensitive = isSensitivePermission(permission);

                    return (
                      <motion.div
                        key={permission}
                        whileHover={{ scale: 1.01 }}
                        className={clsx(
                          "p-3 rounded-lg border-2 transition-all cursor-pointer",
                          status === 'role' && "border-blue-200 bg-blue-50",
                          status === 'added' && "border-green-200 bg-green-50",
                          status === 'removed' && "border-red-200 bg-red-50",
                          status === 'none' && "border-gray-200 bg-white hover:border-gray-300",
                          (readonly || !canEditPermissions) && "cursor-not-allowed opacity-60"
                        )}
                        onClick={() => handlePermissionToggle(permission)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-gray-900">
                                {t(`permissionDescriptions.${permission}`, {
                                  defaultValue: PERMISSION_DESCRIPTIONS[permission],
                                })}
                              </span>
                              {isSensitive && (
                                <span className="text-xs text-amber-600">
                                  <Key className="h-3 w-3 inline" />
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-gray-500 mt-1">
                              {permission}
                            </p>
                          </div>

                          <div className="flex items-center gap-2">
                            {status === 'role' && (
                              <span className="text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded">
                                {statusLabelMap.role}
                              </span>
                            )}
                            {status === 'added' && (
                              <span className="text-xs text-green-600 bg-green-100 px-2 py-1 rounded">
                                {statusLabelMap.added}
                              </span>
                            )}
                            {status === 'removed' && (
                              <span className="text-xs text-red-600 bg-red-100 px-2 py-1 rounded">
                                {statusLabelMap.removed}
                              </span>
                            )}

                            {effectivePermissions.includes(permission) ? (
                              <Check className="h-4 w-4 text-green-600" />
                            ) : (
                              <X className="h-4 w-4 text-gray-400" />
                            )}
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="card card-comfortable bg-gray-50">
        <div className="flex items-center gap-2 mb-4">
          <Shield className="h-5 w-5 text-gray-600" />
          <h4 className="text-lg font-semibold text-gray-900">
            {t('permissionManager.permissionSummary')}
          </h4>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          <div>
            <p className="text-2xl font-bold text-blue-600">
              {roleDefaultPermissions.length}
            </p>
            <p className="text-sm text-gray-600">{t('permissionManager.rolePermission')}</p>
          </div>
          <div>
            <p className="text-2xl font-bold text-green-600">
              {customPermissions.filter(p => !roleDefaultPermissions.includes(p)).length}
            </p>
            <p className="text-sm text-gray-600">{t('permissionManager.addedPermission')}</p>
          </div>
          <div>
            <p className="text-2xl font-bold text-red-600">
              {customPermissions.filter(p => roleDefaultPermissions.includes(p)).length}
            </p>
            <p className="text-sm text-gray-600">{t('permissionManager.removedPermission')}</p>
          </div>
          <div>
            <p className="text-2xl font-bold text-gray-900">
              {effectivePermissions.length}
            </p>
            <p className="text-sm text-gray-600">{t('permissionManager.totalPermission')}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PermissionManager;
